import React, { useState, useEffect, useContext, useRef } from "react";
import { useNavigate } from "react-router-dom";
import Axios from "axios";
import toast from "react-hot-toast";
import UserContext from "../../context/UserContext";
import { buildUserState } from "../../utilities/tokenUtils";
import SearchForm from "../archive/SearchForm";
import ArchiveList from "../archive/ArchiveList";
import Pagination from "../archive/Pagination";
import DetailModal from "../archive/DetailModal";

export default function Archive() {
  const [items, setItems] = useState([]);
  const [isLoading, setIsLoading] = useState(false);
  const [pagination, setPagination] = useState({
    page: 1,
    pageSize: 20,
    totalItems: 0,
    totalPages: 0,
  });
  const [searchParams, setSearchParams] = useState({
    searchText: "",
    author: "",
    sourceCredit: "",
    id: null,
    keywords: "",
    uploadDateStart: null,
    uploadDateEnd: null,
    captureDateStart: null,
    captureDateEnd: null,
    itemType: "all",
  });
  const [selectedItem, setSelectedItem] = useState(null);
  const [showModal, setShowModal] = useState(false);

  const { userData, setUserData } = useContext(UserContext);
  const navigate = useNavigate();
  const searchAbortRef = useRef(null);

  useEffect(() => {
    if (!userData.token) {
      navigate("/admin/login");
    }
  }, [navigate, userData.token]);

  useEffect(() => {
    if (userData.token) {
      performSearch();
    }
  }, [pagination.page, searchParams, userData.token]);

  useEffect(() => () => {
    if (searchAbortRef.current) {
      searchAbortRef.current.abort();
    }
  }, []);

  const performSearch = async (params = searchParams, page = pagination.page) => {
    // Cancelar la búsqueda anterior si sigue en vuelo
    if (searchAbortRef.current) {
      searchAbortRef.current.abort();
    }
    const controller = new AbortController();
    searchAbortRef.current = controller;

    try {
      setIsLoading(true);

      const response = await Axios.post(
        `${process.env.REACT_APP_BACK_DOMAIN}/archive/search`,
        {
          ...params,
          page,
          pageSize: pagination.pageSize,
        },
        {
          headers: { "x-auth-token": userData.token },
          signal: controller.signal,
        }
      );

      setItems(response.data.items);
      setPagination(response.data.pagination);
    } catch (err) {
      if (Axios.isCancel(err) || err.code === "ERR_CANCELED" || err.name === "CanceledError") {
        return;
      }

      console.error("Error searching archive:", err);

      if (err.response?.status === 401) {
        localStorage.removeItem("auth-token");
        sessionStorage.removeItem("partial-token");
        sessionStorage.removeItem("setup-token");
        setUserData(buildUserState(undefined));
        navigate("/admin/login");
        return;
      }

      if (err.response?.status === 500 && err.response?.data?.error) {
        toast.error("No se pudo conectar a la base de datos. Verifica la configuración.");
      } else if (err.response?.data?.msg) {
        toast.error(err.response.data.msg);
      } else {
        toast.error("Error al buscar en el archivo");
      }
    } finally {
      if (!controller.signal.aborted) {
        setIsLoading(false);
      }
    }
  };

  const handleSearch = (params) => {
    setSearchParams(params);
    setPagination((prev) => ({ ...prev, page: 1 }));
  };

  const handlePageChange = (newPage) => {
    setPagination((prev) => ({ ...prev, page: newPage }));
    window.scrollTo({ top: 0, behavior: "smooth" });
  };

  const handleItemClick = (item) => {
    setSelectedItem(item);
    setShowModal(true);
  };

  const handleCloseModal = () => {
    setShowModal(false);
    setSelectedItem(null);
  };

  return (
    <div className="page page--archive">
      <div className="container container--wide">
        <h1>Archivo GN4</h1>
        <p className="page-description">
          Sistema de búsqueda y visualización del archivo histórico de imágenes y historias
        </p>

        <SearchForm onSearch={handleSearch} isLoading={isLoading} />

        {items.length > 0 && (
          <div className="results-info">
            <p>
              Mostrando {items.length} de {pagination.totalItems} resultados
              {searchParams.searchText && ` para "${searchParams.searchText}"`}
            </p>
          </div>
        )}

        <ArchiveList
          items={items}
          onItemClick={handleItemClick}
          isLoading={isLoading}
        />

        {pagination.totalPages > 1 && (
          <Pagination
            currentPage={pagination.page}
            totalPages={pagination.totalPages}
            onPageChange={handlePageChange}
            isLoading={isLoading}
          />
        )}

        {showModal && (
          <DetailModal
            item={selectedItem}
            onClose={handleCloseModal}
            token={userData.token}
          />
        )}
      </div>
    </div>
  );
}
